import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, Modal } from 'react-native';
import { openLegalUrl } from '../constants';
import {
  decidePending,
  describeUpdate,
  fetchLegalManifest,
  readAcceptedVersions,
  recordAcceptance,
  syncSilentlyAcceptedVersions,
  titleFor,
  type PendingAcceptance,
} from '../utils/legalManifest';
import { useThemedStyles } from '../theme/useThemedStyles';
import type { Theme } from '../theme/types';

export default function LegalUpdateBanner() {
  const [pending, setPending] = useState<PendingAcceptance | null>(null);
  const styles = useThemedStyles(createStyles);

  useEffect(() => {
    (async () => {
      const [storedTerms, storedPrivacy] = await readAcceptedVersions();
      const manifest = await fetchLegalManifest();
      if (!manifest) return; // offline / malformed — don't nag

      const next = decidePending(storedTerms, storedPrivacy, manifest);
      if (!next) {
        await syncSilentlyAcceptedVersions(storedTerms, storedPrivacy, manifest);
        return;
      }
      setPending(next);
    })();
  }, []);

  const handleAcknowledge = async () => {
    if (!pending) return;
    try {
      await recordAcceptance(pending);
    } catch {
      // Best-effort; banner will reappear next launch if this fails
    }
    setPending(null);
  };

  if (!pending) return null;

  const effectiveDate = pending.termsNeedsReaccept
    ? pending.manifest.termsVersion
    : pending.manifest.privacyVersion;

  return (
    <Modal
      visible
      transparent
      animationType="fade"
      statusBarTranslucent
      testID="legal-update-modal"
    >
      <View style={styles.overlay}>
        <View style={styles.card}>
          <Text style={styles.title}>{titleFor(pending)}</Text>
          <Text style={styles.body}>
            {describeUpdate(pending)} (effective {effectiveDate}). Please review the changes before
            continuing.
          </Text>
          <View style={styles.linkRow}>
            {pending.termsNeedsReaccept && (
              <TouchableOpacity
                onPress={() => openLegalUrl('terms')}
                accessibilityLabel="View Terms of Service"
                accessibilityRole="link"
                testID="legal-update.terms-link"
              >
                <Text style={styles.linkText}>Terms of Service</Text>
              </TouchableOpacity>
            )}
            {pending.privacyNeedsReaccept && (
              <TouchableOpacity
                onPress={() => openLegalUrl('privacy')}
                accessibilityLabel="View Privacy Policy"
                accessibilityRole="link"
                testID="legal-update.privacy-link"
              >
                <Text style={styles.linkText}>Privacy Policy</Text>
              </TouchableOpacity>
            )}
          </View>
          <TouchableOpacity
            style={styles.acknowledgeButton}
            onPress={handleAcknowledge}
            accessibilityLabel="I Acknowledge"
            accessibilityRole="button"
            testID="legal-update.acknowledge-button"
          >
            <Text style={styles.acknowledgeText} accessible={false}>
              I Acknowledge
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (theme: Theme) => ({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    padding: 24,
  },
  card: {
    backgroundColor: theme.colors.background,
    borderRadius: 16,
    padding: 24,
    width: '100%' as const,
    maxWidth: 400,
    ...theme.shadows.md,
  },
  title: {
    fontSize: 18,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    marginBottom: 12,
  },
  body: {
    fontSize: 14,
    lineHeight: 22,
    color: theme.colors.textSecondary,
    marginBottom: 16,
  },
  linkRow: {
    flexDirection: 'row' as const,
    gap: 16,
    marginBottom: 20,
  },
  linkText: {
    fontSize: 14,
    fontWeight: '600' as const,
    color: theme.colors.primary,
    textDecorationLine: 'underline' as const,
  },
  acknowledgeButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center' as const,
  },
  acknowledgeText: {
    color: theme.colors.background,
    fontSize: 16,
    fontWeight: '700' as const,
  },
});
